<script lang="ts" setup generic="V = any">
import { type VNode, inject } from 'vue';
import { hasSlotContent } from '../../utils';
import { SEGMENTED_CONTROL_CONTEXT } from './shared';

interface Props {
  value?: V;
  disabled?: boolean;
}

const props = defineProps<Props>();

defineSlots<{
  leadingVisual?: () => VNode;
  default?: () => VNode;
}>();

const api = inject(SEGMENTED_CONTROL_CONTEXT);

function onClick() {
  if (props.disabled) {
    return;
  }

  api?.selectValue(props.value);
}
</script>

<template>
  <button
    type="button"
    :class="[$style.root, { [$style.selected]: api?.isValueSelected(props.value), [$style.disabled]: props.disabled }]"
    :disabled="props.disabled"
    @click="onClick()"
  >
    <span v-if="hasSlotContent($slots.leadingVisual)" :class="$style.leadingVisual">
      <slot name="leadingVisual" />
    </span>
    <span v-if="hasSlotContent($slots.default)" :class="$style.label">
      <slot />
    </span>
  </button>
</template>

<style lang="scss" module>
@use '../../styles/shared';

.root {
  @include shared.reset-button();
  display: inline-flex;
  height: 24px;
  gap: 4px;
  padding: 4px;
  border-radius: 4px;

  &:hover:not(.disabled):not(.selected) {
    background: rgba(35, 57, 93, 0.05);
  }
}

.selected {
  background: #fff;
}

.disabled {
  cursor: not-allowed;
  color: #d2d3d9;
}

.leadingVisual {
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg,
  > img {
    width: 100%;
    height: 100%;
  }
}

.label {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
}
</style>
